/**
 * RelatorioBigFive - Componente para gerar relatório PDF do teste Big Five
 * Segue modelo oficial de relatório de personalidade
 */
import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, 
  Download, 
  Loader2, 
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Perfis Big Five com descrições completas (OCEAN model)
const BIG_FIVE_PROFILES = {
  O: {
    nome: 'Abertura à Experiência',
    nomeEn: 'Openness',
    cor: '#8b5cf6',
    descricao: 'Reflete a abertura para novas ideias, criatividade, curiosidade intelectual e apreciação por arte e imaginação.',
    alto: {
      caracteristicas: 'Curioso, criativo, aberto a novas experiências, imaginativo e com interesses amplos.',
      comportamentos: ['Busca novidades e experiências diferentes', 'Aprecia arte, estética e beleza', 'Pensa de forma abstrata e criativa', 'Questiona tradições e convenções'],
      ambiente: 'Ambientes que permitem inovação, criatividade e exploração de novas ideias.'
    },
    baixo: {
      caracteristicas: 'Prático, convencional, prefere rotinas familiares e tem interesses mais focados.',
      comportamentos: ['Prefere métodos comprovados', 'Valoriza estabilidade e previsibilidade', 'Foca no concreto e prático', 'Segue procedimentos estabelecidos'],
      ambiente: 'Ambientes estruturados com processos claros e tarefas bem definidas.'
    }
  },
  C: {
    nome: 'Conscienciosidade',
    nomeEn: 'Conscientiousness',
    cor: '#3b82f6',
    descricao: 'Reflete organização, autodisciplina, responsabilidade e orientação para objetivos.',
    alto: {
      caracteristicas: 'Organizado, disciplinado, confiável, orientado a metas e persistente.',
      comportamentos: ['Planeja e organiza atividades', 'Cumpre prazos e compromissos', 'Mantém alto padrão de qualidade', 'É metódico e detalhista'],
      ambiente: 'Ambientes com metas claras, processos definidos e reconhecimento por resultados.'
    },
    baixo: {
      caracteristicas: 'Flexível, espontâneo, adaptável e menos focado em detalhes.',
      comportamentos: ['Adapta-se facilmente a mudanças', 'Prefere flexibilidade a rigidez', 'Foca no panorama geral', 'Lida bem com ambiguidade'],
      ambiente: 'Ambientes dinâmicos que valorizam adaptabilidade e pensamento rápido.'
    }
  },
  E: {
    nome: 'Extroversão',
    nomeEn: 'Extraversion',
    cor: '#f59e0b',
    descricao: 'Reflete sociabilidade, assertividade, energia e tendência a buscar estímulos na companhia de outros.',
    alto: {
      caracteristicas: 'Sociável, assertivo, energético, comunicativo e entusiasmado.',
      comportamentos: ['Busca interações sociais', 'Expressa opiniões abertamente', 'Assume liderança em grupos', 'Sente-se energizado por outras pessoas'],
      ambiente: 'Ambientes colaborativos, dinâmicos e com oportunidades de networking.'
    },
    baixo: {
      caracteristicas: 'Reservado, introspectivo, independente e prefere atividades solitárias.',
      comportamentos: ['Prefere trabalhar sozinho ou em pequenos grupos', 'Pensa antes de falar', 'Valoriza profundidade nas relações', 'Recarrega energia em solidão'],
      ambiente: 'Ambientes mais calmos, com espaço para trabalho independente e reflexão.'
    }
  },
  A: {
    nome: 'Amabilidade',
    nomeEn: 'Agreeableness',
    cor: '#22c55e',
    descricao: 'Reflete cooperação, empatia, confiança nos outros e preocupação com o bem-estar alheio.',
    alto: {
      caracteristicas: 'Cooperativo, empático, confiante, prestativo e harmonioso.',
      comportamentos: ['Busca consenso e harmonia', 'Ajuda os outros prontamente', 'Evita conflitos quando possível', 'Considera perspectivas alheias'],
      ambiente: 'Ambientes colaborativos, com trabalho em equipe e cultura de apoio mútuo.'
    },
    baixo: {
      caracteristicas: 'Competitivo, cético, direto e focado em resultados próprios.',
      comportamentos: ['Defende posições firmemente', 'Questiona ideias e motivos', 'Prioriza objetivos pessoais', 'Não evita confrontos necessários'],
      ambiente: 'Ambientes competitivos que valorizam assertividade e pensamento crítico.'
    }
  },
  N: {
    nome: 'Neuroticismo',
    nomeEn: 'Neuroticism',
    cor: '#ef4444',
    descricao: 'Reflete a tendência a experimentar emoções negativas como ansiedade, raiva e vulnerabilidade ao estresse.',
    alto: {
      caracteristicas: 'Sensível ao estresse, emocionalmente reativo, preocupado e autocrítico.',
      comportamentos: ['Sente emoções intensamente', 'Preocupa-se com possíveis problemas', 'Pode ser autocrítico', 'Reage fortemente a pressão'],
      ambiente: 'Ambientes com suporte emocional, previsibilidade e feedback construtivo.'
    },
    baixo: {
      caracteristicas: 'Emocionalmente estável, calmo, resiliente e equilibrado.',
      comportamentos: ['Mantém calma sob pressão', 'Recupera-se rapidamente de dificuldades', 'Lida bem com críticas', 'Tem humor estável'],
      ambiente: 'Pode prosperar em ambientes de alta pressão e com desafios constantes.'
    }
  }
};

// Ordem de exibição OCEAN
const OCEAN_ORDER = ['O', 'C', 'E', 'A', 'N'];

// Função para determinar se score é alto ou baixo
const getScoreLevel = (score) => {
  if (score >= 60) return 'alto';
  if (score <= 40) return 'baixo';
  return 'moderado';
};

// Componente do relatório para renderização
const RelatorioContent = React.forwardRef(({ data, psicologo }, ref) => {
  const scores = data.scores || { O: 50, C: 50, E: 50, A: 50, N: 50 };
  const dominantProfile = BIG_FIVE_PROFILES[data.dominant];
  
  return (
    <div ref={ref} className="bg-white p-8 text-black" style={{ width: '210mm', minHeight: '297mm', fontFamily: 'Arial, sans-serif' }}>
      {/* Cabeçalho */}
      <div className="border-b-4 border-blue-600 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-blue-900 text-center">
          Relatório de Avaliação de Personalidade
        </h1>
        <h2 className="text-lg text-blue-700 text-center mt-1">Modelo Big Five (OCEAN)</h2>
      </div>

      {/* 1. Dados do Avaliado */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-blue-800 border-b border-blue-300 pb-1 mb-3">
          1. Dados do Avaliado
        </h3>
        <table className="w-full">
          <tbody>
            <tr>
              <td className="py-1 font-semibold w-40">Nome:</td>
              <td className="py-1">{data.nome}</td>
            </tr>
            <tr>
              <td className="py-1 font-semibold">Cargo / Função:</td>
              <td className="py-1">{data.cargo || 'Não informado'}</td>
            </tr>
            <tr>
              <td className="py-1 font-semibold">Data da Avaliação:</td>
              <td className="py-1">{data.dataAvaliacao}</td>
            </tr>
            {data.versao && (
              <tr>
                <td className="py-1 font-semibold">Versão do Teste:</td>
                <td className="py-1">{data.versao}</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* 2. Gráficos de Perfil */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-blue-800 border-b border-blue-300 pb-1 mb-3">
          2. Perfil de Personalidade OCEAN
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          O teste Big Five avalia cinco grandes dimensões da personalidade. Os resultados abaixo mostram a intensidade de cada traço:
        </p>
        
        <div className="space-y-3">
          {OCEAN_ORDER.map((key) => {
            const profile = BIG_FIVE_PROFILES[key];
            const score = scores[key] || 50;
            const level = getScoreLevel(score);
            
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="w-48 font-semibold text-sm" style={{ color: profile.cor }}>
                  {key} - {profile.nome}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                  <div 
                    className="h-6 rounded-full flex items-center justify-end pr-2"
                    style={{ 
                      width: `${Math.max(score, 5)}%`, 
                      backgroundColor: profile.cor 
                    }}
                  >
                    <span className="text-white text-sm font-bold">{score}%</span>
                  </div>
                </div>
                <span className="w-20 text-xs text-gray-500 capitalize">{level}</span>
              </div>
            );
          })}
        </div>

        {/* Legenda */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs">
          <p className="font-semibold mb-1">Interpretação dos Scores:</p>
          <p><strong>Alto (60-100%):</strong> Traço proeminente no comportamento</p>
          <p><strong>Moderado (41-59%):</strong> Traço presente de forma equilibrada</p>
          <p><strong>Baixo (0-40%):</strong> Traço menos expressivo, tendência ao oposto</p>
        </div>
      </section>

      {/* 3. Dimensão Dominante */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-blue-800 border-b border-blue-300 pb-1 mb-3">
          3. Dimensão Dominante: {data.dominant} - {dominantProfile?.nome}
        </h3>
        
        {dominantProfile && (
          <>
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800">O que significa:</h4>
              <p className="text-gray-700 text-sm">{dominantProfile.descricao}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Score Alto - Características:</h4>
                <p className="text-sm text-blue-700 mb-2">{dominantProfile.alto.caracteristicas}</p>
                <ul className="list-disc list-inside text-xs text-blue-600">
                  {dominantProfile.alto.comportamentos.slice(0, 3).map((comp, i) => (
                    <li key={i}>{comp}</li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Score Baixo - Características:</h4>
                <p className="text-sm text-gray-600 mb-2">{dominantProfile.baixo.caracteristicas}</p>
                <ul className="list-disc list-inside text-xs text-gray-500">
                  {dominantProfile.baixo.comportamentos.slice(0, 3).map((comp, i) => (
                    <li key={i}>{comp}</li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </section>

      {/* 4. Análise Detalhada de Cada Dimensão */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-blue-800 border-b border-blue-300 pb-1 mb-3">
          4. Análise por Dimensão
        </h3>
        
        <div className="space-y-3">
          {OCEAN_ORDER.map((key) => {
            const profile = BIG_FIVE_PROFILES[key];
            const score = scores[key] || 50;
            const level = getScoreLevel(score);
            const levelData = level === 'moderado' ? profile.alto : profile[level];
            
            return (
              <div key={key} className="p-3 rounded-lg" style={{ backgroundColor: `${profile.cor}10`, borderLeft: `4px solid ${profile.cor}` }}>
                <div className="flex justify-between items-center mb-1">
                  <h4 className="font-semibold" style={{ color: profile.cor }}>
                    {profile.nome} ({profile.nomeEn})
                  </h4>
                  <Badge className="text-xs" style={{ backgroundColor: profile.cor, color: 'white' }}>
                    {score}% - {level.charAt(0).toUpperCase() + level.slice(1)}
                  </Badge>
                </div>
                <p className="text-xs text-gray-600">
                  {level === 'moderado' 
                    ? `Apresenta equilíbrio nesta dimensão, podendo adaptar-se a diferentes contextos.`
                    : levelData?.caracteristicas
                  }
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 5. Ambiente de Trabalho Recomendado */}
      <section className="mb-6">
        <h3 className="text-lg font-bold text-blue-800 border-b border-blue-300 pb-1 mb-3">
          5. Ambiente de Trabalho Recomendado
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Com base na dimensão dominante:</h4>
            <p className="text-gray-700 text-sm">
              {dominantProfile && (getScoreLevel(scores[data.dominant]) === 'baixo' 
                ? dominantProfile.baixo.ambiente 
                : dominantProfile.alto.ambiente
              )}
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Pontos de desenvolvimento:</h4>
            <ul className="text-sm text-gray-600 list-disc list-inside">
              {OCEAN_ORDER.filter(k => (scores[k] || 50) <= 40).map(key => (
                <li key={key}>Desenvolver {BIG_FIVE_PROFILES[key].nome.toLowerCase()}</li>
              ))}
              {OCEAN_ORDER.filter(k => (scores[k] || 50) <= 40).length === 0 && (
                <li>Perfil equilibrado - manter consistência</li>
              )}
            </ul>
          </div>
        </div>
      </section>

      {/* 6. Combinações de Traços */}
      <section className="mb-8">
        <h3 className="text-lg font-bold text-blue-800 border-b border-blue-300 pb-1 mb-3">
          6. Insights de Combinações
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {/* Alta C + Alta O */}
          {scores.C >= 60 && scores.O >= 60 && (
            <div className="p-2 bg-purple-50 rounded">
              <strong>Criativo e Organizado:</strong> Combina inovação com disciplina para executar ideias.
            </div>
          )}
          {/* Alta E + Alta A */}
          {scores.E >= 60 && scores.A >= 60 && (
            <div className="p-2 bg-green-50 rounded">
              <strong>Líder Colaborativo:</strong> Sociável e cooperativo, excelente para trabalho em equipe.
            </div>
          )}
          {/* Alta C + Baixo N */}
          {scores.C >= 60 && scores.N <= 40 && (
            <div className="p-2 bg-blue-50 rounded">
              <strong>Estável e Produtivo:</strong> Mantém foco e qualidade mesmo sob pressão.
            </div>
          )}
          {/* Alta O + Alta E */}
          {scores.O >= 60 && scores.E >= 60 && (
            <div className="p-2 bg-amber-50 rounded">
              <strong>Inovador Comunicativo:</strong> Gera e compartilha ideias com entusiasmo.
            </div>
          )}
          {/* Perfil geral */}
          <div className="p-2 bg-gray-50 rounded col-span-2">
            <strong>Perfil Geral:</strong> Este perfil combina{' '}
            {OCEAN_ORDER.filter(k => scores[k] >= 60).map(k => BIG_FIVE_PROFILES[k].nome.toLowerCase()).join(', ') || 'traços equilibrados'}
            {OCEAN_ORDER.filter(k => scores[k] >= 60).length > 0 ? ' como pontos fortes' : ''}.
          </div>
        </div>
      </section>

      {/* Rodapé com assinatura */}
      <footer className="mt-auto pt-8 border-t-2 border-gray-300">
        <div className="flex justify-between items-end">
          <div>
            <p className="text-sm text-gray-600">
              Relatório gerado em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              G2eGente - Plataforma de Recrutamento Ético
            </p>
          </div>
          
          {psicologo && (
            <div className="text-right">
              <div className="border-t border-black w-64 mb-2"></div>
              <p className="font-semibold">{psicologo.nome}</p>
              {psicologo.crp && (
                <p className="text-sm text-gray-600">CRP: {psicologo.crp}</p>
              )}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
});

RelatorioContent.displayName = 'RelatorioContent';

export default function RelatorioBigFiveModal({ 
  open, 
  onClose, 
  invite, 
  psychologists = [],
  crpObrigatorio = false
}) {
  const [loading, setLoading] = useState(false);
  const [psicologoSelecionado, setPsicologoSelecionado] = useState(null);
  const [nomeManual, setNomeManual] = useState('');
  const [crpManual, setCrpManual] = useState('');
  const [usarManual, setUsarManual] = useState(psychologists.length === 0);

  const resultado = invite?.resultado?.bigfive_result || invite?.resultado;
  const bigfiveResult = resultado?.scores ? resultado : null;
  
  if (!bigfiveResult) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado não disponível</DialogTitle>
          </DialogHeader>
          <p className="text-slate-500">
            O resultado do teste Big Five não está disponível para este convite.
          </p>
          <DialogFooter>
            <Button onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const dadosRelatorio = {
    nome: invite.nome_destinatario,
    cargo: invite.cargo_funcao || '',
    dataAvaliacao: invite.responded_at 
      ? new Date(invite.responded_at).toLocaleDateString('pt-BR', { 
          day: '2-digit', month: 'long', year: 'numeric' 
        })
      : new Date().toLocaleDateString('pt-BR'),
    dominant: bigfiveResult.dominant || 'O',
    scores: bigfiveResult.scores || { O: 50, C: 50, E: 50, A: 50, N: 50 },
    versao: bigfiveResult.versao || bigfiveResult.tipo || ''
  };

  const getPsicologoData = () => {
    if (usarManual) {
      if (!nomeManual.trim()) return null;
      return { nome: nomeManual.trim(), crp: crpManual.trim() || null };
    }
    if (psicologoSelecionado) {
      const psi = psychologists.find(p => p.id === psicologoSelecionado);
      return psi ? { nome: psi.nome, crp: psi.crp } : null;
    }
    return null;
  };

  const canGenerate = () => {
    const psi = getPsicologoData();
    if (!psi) return false;
    if (crpObrigatorio && !psi.crp) return false;
    return true;
  };

  const handleGenerate = async () => {
    const psicologo = getPsicologoData();
    if (!psicologo) {
      toast.error('Informe o responsável pelo relatório');
      return;
    }
    if (crpObrigatorio && !psicologo.crp) {
      toast.error('CRP é obrigatório para este relatório');
      return;
    }

    setLoading(true);
    try {
      // Renderizar o componente em um elemento oculto
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '210mm';
      document.body.appendChild(tempDiv);

      // Usar React para renderizar
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempDiv);
      
      // Timeout de segurança para evitar travamento
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout na geração do PDF')), 30000);
      });
      
      const generatePromise = new Promise((resolve, reject) => {
        root.render(
          <RelatorioContent 
            ref={(el) => {
              if (el) {
                // Aguardar renderização completa
                requestAnimationFrame(async () => {
                  try {
                    const canvas = await html2canvas(el, {
                      scale: 1.5, // Reduzido de 2 para 1.5 para melhor performance
                      useCORS: true,
                      logging: false,
                      backgroundColor: '#ffffff',
                      allowTaint: true,
                      removeContainer: true,
                      imageTimeout: 5000
                    });
                    
                    const imgData = canvas.toDataURL('image/jpeg', 0.92); // JPEG é mais rápido que PNG
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const imgWidth = 210;
                    const pageHeight = 297;
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;
                    
                    // Handle multi-page if content is long
                    let heightLeft = imgHeight;
                    let position = 0;
                    
                    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
                    heightLeft -= pageHeight;
                    
                    while (heightLeft > 0) {
                      position = heightLeft - imgHeight;
                      pdf.addPage();
                      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
                      heightLeft -= pageHeight;
                    }
                    
                    // Salvar PDF
                    const fileName = `Relatorio_BigFive_${invite.nome_destinatario.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                    pdf.save(fileName);
                    
                    toast.success('Relatório PDF gerado com sucesso!');
                    resolve();
                  } catch (err) {
                    console.error('Erro ao gerar PDF:', err);
                    reject(err);
                  }
                });
              }
            }}
            data={dadosRelatorio}
            psicologo={psicologo}
          />
        );
      });

      // Usar race entre timeout e geração
      await Promise.race([generatePromise, timeoutPromise]);

      // Limpar
      root.unmount();
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Gerar Relatório Big Five
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview dos dados */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-700 mb-3">Dados do Avaliado</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span><strong>Nome:</strong> {invite.nome_destinatario}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <span><strong>Cargo:</strong> {invite.cargo_funcao || 'Não informado'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span><strong>Data:</strong> {dadosRelatorio.dataAvaliacao}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-500" />
                <span><strong>Dominante:</strong></span>
                <Badge className="bg-blue-100 text-blue-700">
                  {dadosRelatorio.dominant} - {BIG_FIVE_PROFILES[dadosRelatorio.dominant]?.nome}
                </Badge>
              </div>
            </div>
            
            {/* Preview dos scores */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-slate-500 mb-2">Scores OCEAN:</p>
              <div className="flex gap-2">
                {OCEAN_ORDER.map(key => (
                  <div 
                    key={key} 
                    className="flex-1 text-center p-2 rounded text-white text-xs font-bold"
                    style={{ backgroundColor: BIG_FIVE_PROFILES[key].cor }}
                  >
                    {key}: {dadosRelatorio.scores[key] || 50}%
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Seleção de Psicólogo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-700">Responsável pelo Relatório</h4>
              {crpObrigatorio && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  CRP Obrigatório
                </Badge>
              )}
            </div>

            {psychologists.length > 0 && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button
                    variant={!usarManual ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUsarManual(false)}
                  >
                    Psicólogo Cadastrado
                  </Button>
                  <Button
                    variant={usarManual ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUsarManual(true)}
                  >
                    Informar Manualmente
                  </Button>
                </div>
              </div>
            )}

            {!usarManual && psychologists.length > 0 ? (
              <div className="space-y-2">
                <Label>Selecione o Psicólogo</Label>
                <select
                  value={psicologoSelecionado || ''}
                  onChange={(e) => setPsicologoSelecionado(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Selecione...</option>
                  {psychologists.map((psi) => (
                    <option key={psi.id} value={psi.id}>
                      {psi.nome} - CRP: {psi.crp}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Responsável *</Label>
                  <Input
                    value={nomeManual}
                    onChange={(e) => setNomeManual(e.target.value)}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CRP {crpObrigatorio && '*'}</Label>
                  <Input
                    value={crpManual}
                    onChange={(e) => setCrpManual(e.target.value.toUpperCase())}
                    placeholder="Ex: 06/123456"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleGenerate}
            disabled={loading || !canGenerate()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Gerando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Gerar PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
